import React from 'react';
import { X, Award, Star, Target, Heart, Brain, FlaskRound as Flask, Trophy, Calculator, Lightbulb, Smile, Flame } from 'lucide-react';
import type { Achievement, UserAchievement } from '../types/achievements';
import AchievementBadge from './AchievementBadge';
import { trackAchievementUnlock } from '../utils/analytics';

interface AchievementsModalProps {
  achievements: Achievement[];
  userAchievements: UserAchievement[];
  onClose: () => void;
}

const AchievementsModal: React.FC<AchievementsModalProps> = ({
  achievements,
  userAchievements,
  onClose
}) => {
  const completedAchievements = new Set(
    userAchievements.map(ua => ua.achievement_id)
  );

  // Group achievements by category
  const categories = {
    general: {
      title: 'General Achievements',
      icon: Star,
      description: 'Like any content to earn these achievements',
      tiers: [
        { requirement: 1, points: 10 },
        { requirement: 10, points: 25 },
        { requirement: 50, points: 100 },
        { requirement: 100, points: 250 }
      ],
      achievements: achievements.filter(a => a.condition.includes('total_likes >='))
    },
    streaks: {
      title: 'Streak Achievements',
      icon: Flame,
      description: 'Visit daily to maintain your streak',
      tiers: [
        { requirement: 2, points: 15 },
        { requirement: 7, points: 50 },
        { requirement: 14, points: 100 },
        { requirement: 30, points: 250 }
      ],
      achievements: achievements.filter(a => a.condition.includes('current_streak >='))
    },
    challenges: {
      title: 'Challenge Achievements',
      icon: Target,
      description: 'Complete daily challenges to earn these',
      tiers: [
        { requirement: 1, points: 20 },
        { requirement: 5, points: 50 },
        { requirement: 15, points: 150 },
        { requirement: 30, points: 300 }
      ],
      achievements: achievements.filter(a => a.condition.includes('total_challenges >='))
    },
    facts: {
      title: 'Useless Facts',
      icon: Brain,
      description: 'Like useless facts to unlock these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('fact_likes >='))
    },
    animals: {
      title: 'Animal Facts',
      icon: Heart,
      description: 'Like animal facts to earn these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('animal_likes >='))
    },
    science: {
      title: 'Science Facts',
      icon: Flask,
      description: 'Like science facts to unlock these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('science_likes >='))
    },
    records: {
      title: 'Pointless Records',
      icon: Trophy,
      description: 'Like pointless records to earn these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('record_likes >='))
    },
    inventions: {
      title: 'Useless Inventions',
      icon: Lightbulb,
      description: 'Like useless inventions to unlock these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('invention_likes >='))
    },
    numbers: {
      title: 'Number Facts',
      icon: Calculator,
      description: 'Like number facts to earn these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('number_likes >='))
    },
    excuses: {
      title: 'Random Excuses',
      icon: Smile,
      description: 'Like random excuses to earn these achievements',
      tiers: [
        { requirement: 5, points: 25 },
        { requirement: 15, points: 75 },
        { requirement: 30, points: 150 }
      ],
      achievements: achievements.filter(a => a.condition.includes('excuse_likes >='))
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black/40 rounded-2xl shadow-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative border border-white/10">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="flex items-center space-x-3 mb-8">
          <Award className="h-8 w-8 text-purple-400" />
          <h2 className="text-2xl font-bold gradient-text">Achievements Gallery</h2>
        </div>

        {/* Points System */}
        <div className="mb-8 bg-black/20 rounded-lg p-4">
          <h3 className="text-lg font-semibold gradient-text mb-3 flex items-center space-x-2">
            <Star className="h-5 w-5" />
            <span>Points System</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-300">
                <Heart className="h-4 w-4 text-red-400" />
                <span>Like any content: <strong>10 points</strong></span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Target className="h-4 w-4 text-green-400" />
                <span>Complete daily challenge: <strong>20 points</strong></span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-300">
                <Award className="h-4 w-4 text-purple-400" />
                <span>Unlock achievements: <strong>10-300 points</strong></span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Flame className="h-4 w-4 text-orange-400" />
                <span>Daily streak bonuses: <strong>15-250 points</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Categories */}
        <div className="space-y-8">
          {Object.entries(categories).map(([key, category]) => (
            <div key={key} className="bg-black/20 rounded-lg p-6 border border-white/5">
              <div className="flex items-center space-x-3 mb-4">
                <category.icon className="h-6 w-6 text-purple-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">{category.title}</h3>
                  <p className="text-sm text-gray-400">{category.description}</p>
                </div>
              </div>

              {/* Achievement Tiers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
                {category.tiers?.map((tier, index) => (
                  <div key={index} className="bg-black/20 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-300">Tier {index + 1}</span>
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-500/20 text-purple-300">
                        {tier.points} points
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {category.title === 'Streak Achievements' 
                        ? `Maintain a ${tier.requirement}-day streak`
                        : category.title === 'Challenge Achievements'
                        ? `Complete ${tier.requirement} daily challenge${tier.requirement === 1 ? '' : 's'}`
                        : `Like ${tier.requirement} ${category.title.toLowerCase()}`}
                    </p>
                  </div>
                ))}
              </div>

              {/* Achievement Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {category.achievements.map(achievement => {
                  const isCompleted = completedAchievements.has(achievement.id);
                  const userAchievement = userAchievements.find(
                    ua => ua.achievement_id === achievement.id
                  );

                  return (
                    <div 
                      key={achievement.id}
                      className={`rounded-lg p-4 transition ${
                        isCompleted 
                          ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20' 
                          : 'bg-black/20 border border-white/5'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        <AchievementBadge 
                          icon={achievement.badge_icon}
                          className={isCompleted ? 'opacity-100' : 'opacity-50'}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-white mb-1">{achievement.title}</h4>
                          <p className="text-sm text-gray-400 mb-2">
                            {achievement.description}
                          </p>
                          <div className="flex items-center space-x-3">
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-500/20 text-purple-300">
                              {achievement.points} points
                            </span>
                            {isCompleted && userAchievement && (
                              <span className="text-xs text-gray-400">
                                Completed {new Date(userAchievement.completed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementsModal;